import { useEffect, useState } from "react";
import { Trash, Warning, ShieldCheck, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * TestAccountCleanupCard — admin-only tool to physically delete leftover
 * pytest accounts + their cascade rows from the live database.
 *
 * Design intent:
 *   1. Show a *preview* (count + sample + cascade impact) before any
 *      destructive call so an admin never deletes blindly.
 *   2. Gate the destructive path behind a typed-in confirmation phrase
 *      to prevent accidental clicks.
 *   3. Surface the matched test domains so the admin can see exactly
 *      WHY a user is flagged as test (and add a missing domain by
 *      raising it with engineering instead of clicking through hope).
 *
 * Backend: GET /admin/cleanup/test-accounts/preview · POST .../cleanup/test-accounts
 */
const CONFIRM_PHRASE = "DELETE TEST";

export default function TestAccountCleanupCard() {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [confirmInput, setConfirmInput] = useState("");
  const [running, setRunning] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/cleanup/test-accounts/preview");
      setPreview(data);
    } catch (e) {
      toast.error("Couldn't load test-account preview");
      setPreview(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const handleCleanup = async () => {
    if (confirmInput.trim() !== CONFIRM_PHRASE) return;
    if (!window.confirm(
      `Permanently delete ${preview?.user_count || 0} test users + cascade rows?\n\nThis cannot be undone.`
    )) return;
    setRunning(true);
    try {
      const { data } = await api.post("/admin/cleanup/test-accounts");
      const total = Object.values(data.deleted || {}).reduce((a, b) => a + b, 0);
      toast.success(`Removed ${data.deleted?.users || 0} users + ${total - (data.deleted?.users || 0)} cascade rows`);
      setConfirmInput("");
      refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cleanup failed");
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border border-border bg-card p-5" data-testid="test-cleanup-card">
        <div className="text-xs text-muted-foreground">Loading test-account preview…</div>
      </div>
    );
  }
  if (!preview) return null;

  const count = Number(preview.user_count || 0);
  const cascade = preview.cascade_counts || {};
  const cascadeTotal = Object.values(cascade).reduce((a, b) => a + b, 0);
  const isEmpty = count === 0;

  return (
    <div
      className="rounded-2xl border border-border bg-card p-5"
      data-testid="test-cleanup-card"
    >
      <div className="flex items-start gap-3">
        <div className={`flex-none h-11 w-11 rounded-xl flex items-center justify-center ${
          isEmpty ? "bg-emerald-500/10 text-emerald-600" : "bg-amber-500/10 text-amber-600"
        }`}>
          {isEmpty ? <ShieldCheck size={20} weight="duotone" /> : <Warning size={20} weight="duotone" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
            Test-account hygiene
          </div>
          <h3 className="font-display font-bold text-lg leading-snug mt-0.5">
            {isEmpty
              ? "Database is clean — no test accounts found"
              : `${count} test account${count === 1 ? "" : "s"} found`}
          </h3>
          <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
            Matches accounts whose email ends with one of: {" "}
            <span className="font-mono text-foreground">
              {(preview.domains_filtered || []).join(", ")}
            </span>
          </p>
        </div>
        <button
          type="button"
          onClick={refresh}
          className="flex-none h-8 px-2.5 rounded-full text-xs font-semibold text-muted-foreground hover:text-foreground hover:bg-muted transition inline-flex items-center gap-1"
          data-testid="test-cleanup-refresh"
        >
          <MagnifyingGlass size={12} weight="bold" /> Refresh
        </button>
      </div>

      {!isEmpty && (
        <>
          {/* By role + by domain breakdown */}
          <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                By role
              </div>
              <ul className="space-y-1">
                {Object.entries(preview.by_role || {}).sort((a, b) => b[1] - a[1]).map(([role, n]) => (
                  <li key={role} className="flex justify-between tabular-nums">
                    <span className="capitalize">{role}</span>
                    <span className="font-semibold">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                By domain
              </div>
              <ul className="space-y-1">
                {Object.entries(preview.by_domain || {}).sort((a, b) => b[1] - a[1]).map(([dom, n]) => (
                  <li key={dom} className="flex justify-between gap-2 tabular-nums">
                    <span className="font-mono text-[11px] truncate">{dom}</span>
                    <span className="font-semibold">{n}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Cascade impact */}
          {cascadeTotal > 0 && (
            <div className="mt-3 rounded-xl border border-border p-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold mb-1.5">
                Cascade impact
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-x-3 gap-y-1 text-xs tabular-nums" data-testid="test-cleanup-cascade">
                {Object.entries(cascade).filter(([_, n]) => n > 0).sort((a, b) => b[1] - a[1]).map(([coll, n]) => (
                  <div key={coll} className="flex justify-between gap-2">
                    <span className="text-muted-foreground truncate">{coll}</span>
                    <span className="font-semibold">{n}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Sample table */}
          {Array.isArray(preview.sample) && preview.sample.length > 0 && (
            <details className="mt-3 group">
              <summary className="text-xs font-semibold text-muted-foreground cursor-pointer select-none hover:text-foreground py-1">
                Sample (first {preview.sample.length} of {count})
              </summary>
              <div className="mt-2 max-h-56 overflow-y-auto rounded-xl border border-border">
                <table className="w-full text-xs">
                  <tbody>
                    {preview.sample.map((row, idx) => (
                      <tr key={idx} className="border-b border-border last:border-b-0">
                        <td className="px-3 py-1.5 font-mono text-[11px] text-muted-foreground truncate max-w-[260px]">{row.email}</td>
                        <td className="px-3 py-1.5 capitalize text-muted-foreground">{row.role}</td>
                        <td className="px-3 py-1.5 text-muted-foreground tabular-nums">{(row.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}

          {/* Confirmation + delete */}
          <div className="mt-4 pt-4 border-t border-border">
            <label className="text-xs text-muted-foreground leading-relaxed block">
              Type <span className="font-mono font-semibold text-foreground">{CONFIRM_PHRASE}</span> to confirm — this is irreversible.
            </label>
            <div className="mt-2 flex gap-2">
              <input
                type="text"
                value={confirmInput}
                onChange={(e) => setConfirmInput(e.target.value)}
                placeholder={CONFIRM_PHRASE}
                className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm font-mono"
                data-testid="test-cleanup-confirm-input"
              />
              <button
                type="button"
                onClick={handleCleanup}
                disabled={confirmInput.trim() !== CONFIRM_PHRASE || running}
                className="h-9 px-4 rounded-lg bg-destructive text-destructive-foreground text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-destructive/90 transition"
                data-testid="test-cleanup-delete-button"
              >
                <Trash size={14} weight="duotone" />
                {running ? "Deleting…" : `Delete ${count}`}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
